import { useState, useEffect, useMemo, useRef, useCallback, type CSSProperties, type SyntheticEvent } from "react";
import { Link } from "react-router-dom";

// Transform Supabase Storage URL to use image transformation for optimized delivery
const getOptimizedImageUrl = (src: string, targetWidth: number): string => {
  // Only transform Supabase storage URLs
  if (!src.includes("supabase.co/storage/v1/object/public/")) {
    return src;
  }

  // Convert object URL to render URL with transformation
  // From: .../storage/v1/object/public/bucket/path
  // To: .../storage/v1/render/image/public/bucket/path?width=X&resize=contain
  const transformedUrl = src.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/");

  // Use 2x for retina displays, cap at reasonable max
  const optimizedWidth = Math.min(Math.round(targetWidth * 2), 1440);

  return `${transformedUrl}?width=${optimizedWidth}&resize=contain&quality=85`;
};

interface GalleryImage {
  src: string;
  alt: string;
  /** Optional title attribute for SEO */
  title?: string;
  /** Optional known dimensions. If omitted, we infer from the image on load. */
  width?: number;
  height?: number;
  /** Optional link to navigate to when clicking the image */
  link?: string;
}

type PositionedItem = {
  left: number;
  top: number;
  width: number;
  height: number;
  loaded: boolean;
};

interface MasonryGalleryProps {
  images: GalleryImage[];
  /** Fixed number of columns (2 or 3), or 'mixed' for alternating rows. If not set, uses responsive defaults. */
  columnCount?: 2 | 3 | "mixed";
  /** Gap size between images. 'large' provides more spacing for home page. */
  gapSize?: "default" | "large";
}

const MasonryGallery = ({ images, columnCount, gapSize = "default" }: MasonryGalleryProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const isMixed = columnCount === "mixed";

  // Track intrinsic aspect ratios as they load (h/w)
  const [ratios, setRatios] = useState<number[]>(() =>
    images.map((img) => (img.width && img.height ? img.height / img.width : 1)),
  );
  const [loaded, setLoaded] = useState<boolean[]>(() => images.map(() => false));

  // Responsive columns similar to Squarespace feel
  const getColumnCount = (w: number) => {
    // Mixed mode uses its own logic
    if (isMixed) return 0; // Not used for mixed
    // If columnCount is specified, use it (with mobile fallback)
    if (columnCount) {
      if (w < 640) return Math.min(columnCount, 2); // mobile max 2 columns
      return columnCount;
    }
    // Default responsive behavior
    if (w < 640) return 2; // mobile - 2 columns
    if (w < 1024) return 2; // tablet
    return 3; // desktop
  };

  const getGap = (w: number) => {
    if (gapSize === "large") {
      if (w < 640) return 24; // larger gap on mobile
      return 48; // larger gap on desktop/tablet
    }
    if (w < 640) return 16; // default gap on mobile
    return 32; // default gap on desktop/tablet
  };

  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [columns, setColumns] = useState<number>(isMixed ? 0 : typeof columnCount === "number" ? columnCount : 3);
  const [gap, setGap] = useState<number>(32);

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    setContainerWidth(w);
    setColumns(getColumnCount(w));
    setGap(getGap(w));
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);
    const onWin = () => measure();
    window.addEventListener("resize", onWin);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onWin);
    };
  }, [measure]);

  // Compute item width (not used for mixed mode)
  const itemWidth = useMemo(() => {
    if (isMixed) return 0; // Mixed mode calculates per-row
    if (!containerWidth || columns <= 0) return 0;
    const totalGaps = gap * (columns - 1);
    return (containerWidth - totalGaps) / columns;
  }, [containerWidth, columns, gap, isMixed]);

  // Compute positioned items using a simple masonry algorithm
  const { positions, containerHeight } = useMemo(() => {
    // Mixed mode: alternating rows of 2 and 3 columns
    if (isMixed && containerWidth) {
      const positions: PositionedItem[] = [];
      let currentY = 0;
      let imageIndex = 0;
      let rowIndex = 0;
      const isMobile = containerWidth < 640;

      while (imageIndex < images.length) {
        // Alternate between 3 and 2 columns (start with 3)
        // On mobile, alternate between 2 and 1
        const rowCols = isMobile ? (rowIndex % 2 === 0 ? 2 : 1) : rowIndex % 2 === 0 ? 3 : 2;
        const rowItemWidth = (containerWidth - gap * (rowCols - 1)) / rowCols;

        // Get images for this row
        const rowImages: number[] = [];
        for (let c = 0; c < rowCols && imageIndex < images.length; c++) {
          rowImages.push(imageIndex);
          imageIndex++;
        }

        // Calculate max height for this row based on aspect ratios
        let maxRowHeight = 0;
        rowImages.forEach((idx) => {
          const ratio = ratios[idx] || 1;
          const height = Math.round(rowItemWidth * ratio);
          if (height > maxRowHeight) maxRowHeight = height;
        });

        // Position each image in the row
        rowImages.forEach((idx, colIdx) => {
          const ratio = ratios[idx] || 1;
          const height = Math.round(rowItemWidth * ratio);
          positions[idx] = {
            left: colIdx * (rowItemWidth + gap),
            top: currentY,
            width: Math.round(rowItemWidth),
            height: height,
            loaded: loaded[idx],
          };
        });

        currentY += maxRowHeight + gap;
        rowIndex++;
      }

      const containerHeight = Math.max(0, currentY - gap);
      return { positions, containerHeight };
    }

    // Standard masonry algorithm
    if (!itemWidth) return { positions: [] as PositionedItem[], containerHeight: 0 };
    const colHeights = new Array(columns).fill(0) as number[];
    const positions: PositionedItem[] = images.map((img, i) => {
      const ratio = ratios[i] || 1; // height/width
      const targetHeight = Math.round(itemWidth * ratio);
      // find the column with min height
      let colIndex = 0;
      for (let c = 1; c < columns; c++) {
        if (colHeights[c] < colHeights[colIndex]) colIndex = c;
      }
      const left = colIndex * (itemWidth + gap);
      const top = colHeights[colIndex];
      colHeights[colIndex] += targetHeight + gap;

      return {
        left,
        top,
        width: Math.round(itemWidth),
        height: targetHeight,
        loaded: loaded[i],
      } as PositionedItem;
    });
    const containerHeight = Math.max(0, ...colHeights) - gap; // remove last gap
    return { positions, containerHeight };
  }, [images, itemWidth, columns, ratios, loaded, gap, isMixed, containerWidth]);

  useEffect(() => {
    // Flip ready on first layout - for mixed mode, use containerWidth instead of itemWidth
    if ((isMixed ? containerWidth : itemWidth) && images.length) setReady(true);
  }, [itemWidth, images.length, isMixed, containerWidth]);

  const pendingUpdates = useRef<{ index: number; ratio: number }[]>([]);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const flushUpdates = useCallback(() => {
    if (pendingUpdates.current.length === 0) return;

    const updates = [...pendingUpdates.current];
    pendingUpdates.current = [];

    setRatios((prev) => {
      const next = [...prev];
      let changed = false;
      updates.forEach(({ index, ratio }) => {
        if (next[index] !== ratio) {
          next[index] = ratio;
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setLoaded((prev) => {
      const next = [...prev];
      let changed = false;
      updates.forEach(({ index }) => {
        if (!next[index]) {
          next[index] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    debounceTimer.current = null;
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const onImgLoad = (i: number, e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const naturalW = img.naturalWidth || images[i].width || 1;
    const naturalH = img.naturalHeight || images[i].height || 1;
    const ratio = naturalH / naturalW;

    pendingUpdates.current.push({ index: i, ratio });

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    // Debounce for 50ms to batch rapid loads
    debounceTimer.current = setTimeout(flushUpdates, 50);
  };

  return (
    <div className="px-4 sm:px-8">
      <div
        ref={containerRef}
        className="gallery-masonry-wrapper"
        style={{ position: "relative", width: "100%", margin: "0 auto" }}
      >
        <div
          className={`gallery-masonry-list ${ready ? "gallery-masonry-list--ready" : ""}`}
          style={{ position: "relative", height: containerHeight || 0 }}
        >
          {images.map((image, i) => {
            const pos = positions[i];
            const itemStyle: CSSProperties = pos
              ? {
                  position: "absolute",
                  width: pos.width,
                  transform: `translate3d(${Math.round(pos.left)}px, ${Math.round(pos.top)}px, 0)`,
                }
              : { position: "absolute" };
            const wrapperStyle: CSSProperties = pos
              ? { height: pos.height, overflow: "hidden", aspectRatio: `${pos.width} / ${pos.height}` }
              : { overflow: "hidden", aspectRatio: "4 / 5" };
            const imgStyle: CSSProperties = pos
              ? {
                  display: "block",
                  objectPosition: "50% 50%",
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                }
              : { display: "block", width: "100%", height: "auto" };

            // Get optimized image URL based on display width
            const displayWidth = pos?.width || 400;
            const optimizedSrc = getOptimizedImageUrl(image.src, displayWidth);

            const content = (
              <div className="gallery-masonry-item-wrapper" data-animation-role="image" style={wrapperStyle}>
                <img
                  src={optimizedSrc}
                  alt={image.alt}
                  title={image.title}
                  width={pos?.width || 400}
                  height={pos?.height || 400}
                  loading="lazy"
                  decoding="async"
                  style={imgStyle}
                  onLoad={(e) => onImgLoad(i, e)}
                />
              </div>
            );

            return (
              <figure
                key={i}
                className="gallery-masonry-item"
                data-loaded={pos?.loaded ? "true" : "false"}
                data-show={ready ? "true" : "false"}
                style={itemStyle}
              >
                {image.link ? (
                  <Link to={image.link} className="block transition-opacity hover:opacity-80">
                    {content}
                  </Link>
                ) : (
                  content
                )}
              </figure>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MasonryGallery;
